import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Form, Message, Modal } from 'semantic-ui-react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { X } from 'phosphor-react'
import styled from 'styled-components'
import {
    FlexRow,
    FlexRowBetween,
    LoaderBar,
    TitleText,
} from 'components'
import {
    AppButton,
    AppPrimaryButton,
    FormCheckBoxField,
    FormDropdownField,
    FormTextFormField,
    SubmitCancelButtons,
} from 'elements'
import { GetUserList } from 'hooks/user'
import { useNotification } from 'hooks/toast'
import { ReportApprovalService } from 'utility'
import {
    WORKFLOW_TRIGGER,
    buildWorkflowPayload,
    defaultWorkflowConfig,
    extractApiError,
} from './report-approval-utils'
import {
    ApprovalBreadcrumb,
    ApproverRow,
    FinalPill,
    PageSubtitle,
    ScreenContainer,
    WorkflowCallout,
    WorkflowDivider,
    WorkflowHeaderRow,
    WorkflowHelp,
    WorkflowSectionTitle,
} from './report-approval-styles'

const WorkflowModal = styled(Modal)`
    .content {
        padding: 1.5rem !important;
    }
`

const triggerOptions = [
    {
        key: WORKFLOW_TRIGGER.Immediate,
        value: WORKFLOW_TRIGGER.Immediate,
        text: 'Immediately when report is completed',
    },
    {
        key: WORKFLOW_TRIGGER.AfterFindings,
        value: WORKFLOW_TRIGGER.AfterFindings,
        text: 'When all findings of the inspection are resolved',
    },
    {
        key: WORKFLOW_TRIGGER.Delay,
        value: WORKFLOW_TRIGGER.Delay,
        text: 'After a delay (gives auditor time to edit)',
    },
    {
        key: WORKFLOW_TRIGGER.Manual,
        value: WORKFLOW_TRIGGER.Manual,
        text: 'Manually — auditor clicks "Submit for approval"',
    },
]

const delayUnitOptions = [
    { key: 'hours', value: 'hours', text: 'hours' },
    { key: 'days', value: 'days', text: 'days' },
]

const rejectionOptions = [
    {
        key: 'anyone',
        value: 'anyone',
        text: 'Anyone in the chain can reject (returns to auditor)',
    },
    {
        key: 'final_only',
        value: 'final_only',
        text: 'Only final approver can reject',
    },
]

const reminderOptions = [
    { key: 'off', value: 'off', text: 'Off' },
    { key: 'daily', value: 'daily', text: 'Daily until acted' },
    {
        key: 'every_2_days_3',
        value: 'every_2_days_3',
        text: 'Every 2 days, up to 3 reminders',
    },
]

const escalateOptions = [
    { key: 'off', value: 'off', text: 'Off' },
    {
        key: 'after_5_days',
        value: 'after_5_days',
        text: 'After 5 days, notify template owner + skip to next approver',
    },
]

const defaultFormValues = {
    Enabled: true,
    TriggerType: WORKFLOW_TRIGGER.Immediate,
    DelayValue: 24,
    DelayUnit: 'hours',
    RejectionPolicy: 'anyone',
    ReminderCadence: 'every_2_days_3',
    AutoEscalate: 'after_5_days',
    RegeneratePdf: true,
    IncludeAuditorOnNotifications: true,
    AllowDelegate: false,
}

const initials = name =>
    String(name || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase() || '?'

const toFormValues = config => {
    const source = config || defaultWorkflowConfig()
    return {
        Enabled: !!source.enabled,
        TriggerType: source.triggerType || WORKFLOW_TRIGGER.Immediate,
        DelayValue: source.delayValue || 24,
        DelayUnit: source.delayUnit || 'hours',
        RejectionPolicy: source.rejectionPolicy || 'anyone',
        ReminderCadence: source.reminderCadence || 'every_2_days_3',
        AutoEscalate: source.autoEscalate || 'after_5_days',
        RegeneratePdf: !!source.regeneratePdf,
        IncludeAuditorOnNotifications: !!source.includeAuditorOnNotifications,
        AllowDelegate: false,
    }
}

export const ApprovalWorkflowConfig = ({
    templateId,
    templateName = '',
    config: initialConfig,
    rawConfig,
    loading = false,
    onBack,
}) => {
    const { successToast, errorToast } = useNotification()
    const { data: users = [], options: userOptionsRaw = [] } = GetUserList()
    const [approvers, setApprovers] = useState(initialConfig?.approvers || [])
    const [saving, setSaving] = useState(false)
    const [saveError, setSaveError] = useState('')
    const [userModal, setUserModal] = useState(false)
    const [externalModal, setExternalModal] = useState(false)
    const [selectedUserId, setSelectedUserId] = useState('')
    const [externalName, setExternalName] = useState('')
    const [externalEmail, setExternalEmail] = useState('')

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: defaultFormValues,
    })

    const triggerType = watch('TriggerType')
    const enabled = watch('Enabled')

    useEffect(() => {
        if (!initialConfig) return
        reset(toFormValues(initialConfig))
        setApprovers(initialConfig.approvers || [])
    }, [initialConfig, reset])

    const userOptions = useMemo(
        () =>
            (userOptionsRaw || []).map(opt => ({
                key: String(opt.value),
                value: opt.value,
                text: opt.text,
            })),
        [userOptionsRaw],
    )

    const userLookup = useMemo(() => {
        let list = []
        if (Array.isArray(users)) list = users
        else if (Array.isArray(users?.Data)) list = users.Data
        return list.reduce((acc, user) => {
            const ids = [
                user.UserId,
                user.userId,
                user.unique_id,
                user.UniqueId,
                user.Id,
            ]
            ids.filter(id => id != null && id !== '').forEach(id => {
                acc[String(id)] = user
            })
            return acc
        }, {})
    }, [users])

    const handleDragEnd = result => {
        if (!result.destination) return
        const next = [...approvers]
        const [moved] = next.splice(result.source.index, 1)
        next.splice(result.destination.index, 0, moved)
        setApprovers(next)
    }

    const addUserApprover = () => {
        if (!selectedUserId) {
            errorToast('Please select a user.')
            return
        }
        const user = userLookup[String(selectedUserId)]
        const option = userOptions.find(
            opt => String(opt.value) === String(selectedUserId),
        )
        const resolvedUserId =
            user?.UserId ?? user?.userId ?? selectedUserId
        const already = approvers.some(
            item =>
                String(item.id) === `user-${selectedUserId}` ||
                String(item.userId) === String(selectedUserId) ||
                String(item.userId) === String(resolvedUserId),
        )
        if (already) {
            errorToast('That user is already in the chain.')
            return
        }
        setApprovers([
            ...approvers,
            {
                id: `user-${selectedUserId}`,
                userId: resolvedUserId,
                name: user?.UserName || user?.Name || option?.text || 'Approver',
                email:
                    user?.Email || user?.UserEmail || user?.EmailAddress || '',
                role: user?.Role || '',
                isExternal: false,
            },
        ])
        setSelectedUserId('')
        setUserModal(false)
    }

    const addExternalApprover = () => {
        if (!externalName.trim() || !externalEmail.trim()) {
            errorToast('Name and email are required.')
            return
        }
        // one plain address: the approval link is e-mailed to it
        if (!/^[^\s@,;<>"()]+@[^\s@,;<>"()]+\.[^\s@,;<>"()]+$/.test(externalEmail.trim()) || externalEmail.trim().length > 200) {
            errorToast('Enter one valid e-mail address.')
            return
        }
        setApprovers([
            ...approvers,
            {
                id: `ext-${Date.now()}`,
                userId: null,
                name: externalName.trim(),
                email: externalEmail.trim(),
                role: '',
                isExternal: true,
            },
        ])
        setExternalName('')
        setExternalEmail('')
        setExternalModal(false)
    }

    const submit = async formData => {
        if (saving) return
        if (formData.Enabled && !approvers.length) {
            errorToast('Add at least one approver before saving.')
            return
        }
        if (Number(formData.TriggerType) === WORKFLOW_TRIGGER.Delay) {
            const hours =
                formData.DelayUnit === 'days'
                    ? Number(formData.DelayValue || 0) * 24
                    : Number(formData.DelayValue || 0)
            if (!(hours > 0) || hours > 30 * 24) {
                errorToast('The delay must be between 1 hour and 30 days.')
                return
            }
        }
        try {
            setSaving(true)
            setSaveError('')
            await ReportApprovalService.saveWorkflowConfig(
                templateId,
                buildWorkflowPayload(
                    {
                        enabled: formData.Enabled,
                        triggerType: formData.TriggerType,
                        delayValue: formData.DelayValue,
                        delayUnit: formData.DelayUnit || 'hours',
                        approvers,
                        rejectionPolicy: formData.RejectionPolicy,
                        reminderCadence: formData.ReminderCadence,
                        autoEscalate: formData.AutoEscalate,
                        regeneratePdf: formData.RegeneratePdf,
                        includeAuditorOnNotifications:
                            formData.IncludeAuditorOnNotifications,
                    },
                    rawConfig,
                ),
            )
            successToast('Approval workflow saved')
        } catch (error) {
            const message = extractApiError(error)
            setSaveError(message)
            errorToast(message)
        } finally {
            setSaving(false)
        }
    }

    if (loading && !initialConfig) {
        return <LoaderBar content="Loading workflow…" />
    }

    const lastIndex = approvers.length - 1

    return (
        <ScreenContainer>
            <ApprovalBreadcrumb>
                <button type="button" onClick={onBack}>
                    Templates
                </button>
                {templateName ? (
                    <>
                        {' › '}
                        <span>{templateName}</span>
                    </>
                ) : null}
                {' › '}
                Settings › <strong>Approval Workflow</strong>
            </ApprovalBreadcrumb>

            <WorkflowHeaderRow>
                <div>
                    <h1 style={{ margin: '0 0 4px', fontSize: 22 }}>
                        Approval Workflow
                    </h1>
                    <PageSubtitle>
                        Configure who reviews completed reports and when.
                        Each approver signs the report in turn.
                    </PageSubtitle>
                </div>
                <FlexRow className="gap-2">
                    <Button type="button" onClick={onBack}>
                        Cancel
                    </Button>
                    <AppPrimaryButton
                        type="button"
                        loading={saving}
                        onClick={handleSubmit(submit)}
                    >
                        Save workflow
                    </AppPrimaryButton>
                </FlexRow>
            </WorkflowHeaderRow>

            {saveError && <Message negative>{saveError}</Message>}

            <WorkflowCallout>
                When a report of this template is completed, the first approver
                gets an e-mail with a link to approve or reject it, without
                signing in (the link works once and expires after 7 days).
                Approvers with an eAuditor login can also use Report
                Approvals.
            </WorkflowCallout>

            <Form noValidate>
                <WorkflowSectionTitle>
                    1. Enable + trigger timing
                </WorkflowSectionTitle>
                <Form.Group widths="equal">
                    <Form.Field>
                        <FormCheckBoxField
                            control={control}
                            name="Enabled"
                            label="Enabled"
                            toggle
                            errors={errors.Enabled}
                        />
                    </Form.Field>
                    <FormDropdownField
                        control={control}
                        name="TriggerType"
                        label="When does the workflow trigger?"
                        placeholder="Select trigger"
                        options={triggerOptions}
                        errors={errors.TriggerType}
                    />
                    {Number(triggerType) === WORKFLOW_TRIGGER.Delay ? (
                        <FormTextFormField
                            control={control}
                            name="DelayValue"
                            label="Delay"
                            type="number"
                            placeholder="24"
                            errors={errors.DelayValue}
                        />
                    ) : (
                        <Form.Field />
                    )}
                    {Number(triggerType) === WORKFLOW_TRIGGER.Delay ? (
                        <FormDropdownField
                            control={control}
                            name="DelayUnit"
                            label="Unit"
                            options={delayUnitOptions}
                            errors={errors.DelayUnit}
                        />
                    ) : (
                        <Form.Field />
                    )}
                </Form.Group>
                {Number(triggerType) === WORKFLOW_TRIGGER.Delay ? (
                    <WorkflowHelp>
                        The delay is not applied yet: approvers are notified as
                        soon as the report is completed, as with
                        &quot;Immediately&quot;.
                    </WorkflowHelp>
                ) : null}
                {Number(triggerType) === WORKFLOW_TRIGGER.AfterFindings ? (
                    <WorkflowHelp>
                        The report goes for approval once every finding the
                        inspection raised (the tickets created by this
                        template&apos;s auto-ticket rules) is resolved by the
                        department, or closed / cancelled. An inspection without
                        findings goes for approval as soon as it is completed. If
                        findings are only sent after approval (Settings &gt;
                        Corrective-action tickets), the report goes for approval
                        at completion.
                    </WorkflowHelp>
                ) : null}
                {Number(triggerType) === WORKFLOW_TRIGGER.Manual ? (
                    <WorkflowHelp>
                        Manual submission is not available yet: with this
                        setting no approval is started. Choose
                        &quot;Immediately&quot; to use the workflow.
                    </WorkflowHelp>
                ) : null}
                {approvers.some(item => item.isExternal) ? (
                    <WorkflowHelp>
                        External (e-mail) approvers have no eAuditor login: they
                        approve through the e-mailed link only. If the link
                        expires, use &quot;Resend approval e-mail&quot; on the
                        report.
                    </WorkflowHelp>
                ) : null}

                <WorkflowDivider />

                <WorkflowSectionTitle>2. Approvers (in order)</WorkflowSectionTitle>
                <WorkflowHelp>
                    Drag to reorder. The last approver is the{' '}
                    <strong>final approver</strong> — their approval completes
                    the workflow and (optionally) regenerates the PDF with all
                    signatures stamped.
                </WorkflowHelp>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="approvers">
                        {provided => (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                            >
                                {approvers.map((item, index) => (
                                    <Draggable
                                        key={String(item.id)}
                                        draggableId={String(item.id)}
                                        index={index}
                                    >
                                        {drag => (
                                            <ApproverRow
                                                ref={drag.innerRef}
                                                {...drag.draggableProps}
                                                $final={index === lastIndex}
                                            >
                                                <span
                                                    className="handle"
                                                    {...drag.dragHandleProps}
                                                >
                                                    ⋮⋮
                                                </span>
                                                <span className="step-num">
                                                    {index + 1}
                                                </span>
                                                <span className="avatar">
                                                    {initials(item.name)}
                                                </span>
                                                <div className="meta">
                                                    <div className="name">
                                                        {item.name}
                                                        {item.role
                                                            ? ` — ${item.role}`
                                                            : ''}
                                                        {index ===
                                                            lastIndex && (
                                                            <FinalPill>
                                                                FINAL
                                                            </FinalPill>
                                                        )}
                                                    </div>
                                                    <div className="email">
                                                        {item.email ||
                                                            (item.isExternal
                                                                ? 'External email'
                                                                : '')}
                                                    </div>
                                                </div>
                                                <Form.Dropdown
                                                    selection
                                                    disabled
                                                    compact
                                                    options={[
                                                        {
                                                            key: 'seq',
                                                            value: 'sequential',
                                                            text: 'Sequential',
                                                        },
                                                    ]}
                                                    value="sequential"
                                                />
                                                <AppButton
                                                    type="button"
                                                    size="tiny"
                                                    onClick={() =>
                                                        setApprovers(
                                                            approvers.filter(
                                                                (_, i) =>
                                                                    i !== index,
                                                            ),
                                                        )
                                                    }
                                                >
                                                    Remove
                                                </AppButton>
                                            </ApproverRow>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                {!approvers.length && enabled && (
                    <Message info size="small">
                        Add at least one approver before saving.
                    </Message>
                )}
                <FlexRow className="gap-2 mt-2">
                    <AppButton
                        type="button"
                        onClick={() => setUserModal(true)}
                    >
                        + Add approver (from users)
                    </AppButton>
                    <AppButton
                        type="button"
                        onClick={() => setExternalModal(true)}
                    >
                        + Add external email approver
                    </AppButton>
                </FlexRow>

                <WorkflowDivider />

                <WorkflowSectionTitle>3. Rules &amp; notifications</WorkflowSectionTitle>
                <Form.Group widths="equal">
                    <FormDropdownField
                        control={control}
                        name="RejectionPolicy"
                        label="Rejection policy"
                        options={rejectionOptions}
                        errors={errors.RejectionPolicy}
                    />
                    <FormDropdownField
                        control={control}
                        name="ReminderCadence"
                        label="Reminder cadence"
                        options={reminderOptions}
                        errors={errors.ReminderCadence}
                    />
                    <FormDropdownField
                        control={control}
                        name="AutoEscalate"
                        label="Auto-escalate on stall"
                        options={escalateOptions}
                        errors={errors.AutoEscalate}
                    />
                </Form.Group>

                <Form.Group widths="equal">
                    <Form.Field>
                        <FormCheckBoxField
                            control={control}
                            name="RegeneratePdf"
                            label="Regenerate final PDF on last approval"
                            errors={errors.RegeneratePdf}
                        />
                        <WorkflowHelp>
                            Runs ApprovedPdfStamperService with a certificate
                            page listing every approver. If off, only the last
                            approver&apos;s signature is stamped.
                        </WorkflowHelp>
                    </Form.Field>
                    <Form.Field>
                        <FormCheckBoxField
                            control={control}
                            name="IncludeAuditorOnNotifications"
                            label="Include original auditor on all notifications"
                            errors={errors.IncludeAuditorOnNotifications}
                        />
                    </Form.Field>
                    <Form.Field>
                        <FormCheckBoxField
                            control={control}
                            name="AllowDelegate"
                            label="Allow approvers to delegate"
                            disabled
                            errors={errors.AllowDelegate}
                        />
                    </Form.Field>
                </Form.Group>

                <WorkflowCallout $warn>
                    <strong>How approvals work</strong>
                    <ul>
                        <li>Approvers act one after the other, in this order.</li>
                        <li>
                            A rejection returns the report to the auditor; when
                            it is completed again, approval starts again at
                            step 1.
                        </li>
                        <li>
                            Approval links work once and expire after 7 days. An
                            admin can resend the e-mail from the report.
                        </li>
                        <li>Settings apply to this template only.</li>
                    </ul>
                </WorkflowCallout>
            </Form>

            <WorkflowModal
                open={userModal}
                onClose={() => setUserModal(false)}
                size="tiny"
                closeOnDimmerClick={false}
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <TitleText>Add approver</TitleText>
                        <X
                            className="pointer"
                            size={24}
                            onClick={() => setUserModal(false)}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Dropdown
                            search
                            selection
                            label="User"
                            placeholder="Select user"
                            options={userOptions}
                            value={selectedUserId}
                            onChange={(_, { value }) => setSelectedUserId(value)}
                        />
                        <div className="pt-1">
                            <SubmitCancelButtons
                                onSubmit={addUserApprover}
                                onCancel={() => setUserModal(false)}
                                submitText="Add"
                            />
                        </div>
                    </Form>
                </Modal.Content>
            </WorkflowModal>

            <WorkflowModal
                open={externalModal}
                onClose={() => setExternalModal(false)}
                size="tiny"
                closeOnDimmerClick={false}
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <TitleText>Add external email approver</TitleText>
                        <X
                            className="pointer"
                            size={24}
                            onClick={() => setExternalModal(false)}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Input
                            label="Name"
                            value={externalName}
                            onChange={(_, { value }) => setExternalName(value)}
                        />
                        <Form.Input
                            label="Email"
                            type="email"
                            value={externalEmail}
                            onChange={(_, { value }) =>
                                setExternalEmail(value)
                            }
                        />
                        <div className="pt-1">
                            <SubmitCancelButtons
                                onSubmit={addExternalApprover}
                                onCancel={() => setExternalModal(false)}
                                submitText="Add"
                            />
                        </div>
                    </Form>
                </Modal.Content>
            </WorkflowModal>
        </ScreenContainer>
    )
}
